import React, {Dispatch, SetStateAction, useCallback} from 'react';

import {ImageSelect} from 'src/components/image-select';
import {FormInput} from 'src/components/input';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ValidatedControl} from 'src/contexts/validation-context';
import {SettingsType as SettingsTypes} from 'src/openapi/admin/models/SettingsType';

import {Group} from '@pb/kit-component-group';

import {BANNER_IMAGE_CONTROL} from '../constants';

import {BannerSkeleton} from './banner.skeleton';

import type {BannerSettings} from './useBanner';
import type {Error} from 'src/openapi/admin/models/Error';
import type {SettingsType} from 'src/openapi/admin/models/SettingsType';

type FileWithPreview = File & {preview?: string};

const getFilePreview = (file?: File): string | null => {
    if (file && typeof (file as FileWithPreview).preview === 'string') {
        return (file as FileWithPreview).preview ?? null;
    }

    return null;
};

type Props = {
    type: SettingsType,
    isLoading: boolean,
    loadingError: Error | null,
    readonly?: boolean,

    bannerSettings: BannerSettings,
    setBannerSettings: Dispatch<SetStateAction<BannerSettings>>,
};

export const Banner = (props: Props) => {
    const {
        type,
        isLoading,
        loadingError,
        readonly,
        bannerSettings,
        setBannerSettings,
    } = props;

    const removeBannerImage = useCallback(() => {
        setBannerSettings(prevState => ({...prevState, content: undefined}));
    }, [setBannerSettings]);

    const setBannerImage = useCallback((file: File) => {
        setBannerSettings(prevState => ({...prevState, content: file}));
    }, [setBannerSettings]);

    const setBannerLink = useCallback((link: string) => {
        setBannerSettings(prevState => ({...prevState, link: link ? link : undefined}));
    }, [setBannerSettings]);

    const downloadAdvertisementImage = useCallback(() => {
        if (bannerSettings.content) {
            const anchor = document.createElement('a');
            const preview = getFilePreview(bannerSettings.content);

            if (preview) {
                anchor.href = preview;
                anchor.download = bannerSettings.content.name;
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
            }
        }
    }, [bannerSettings]);

    return (
        <Skeleton shown={isLoading} view={<BannerSkeleton/>}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}>
                <Group direction="v">
                    <ValidatedControl name={`${type}-${BANNER_IMAGE_CONTROL}`}>
                        {() => (
                            <ImageSelect
                                readonly={readonly}
                                width={360}
                                height={150}
                                maxSize={2}
                                hint="Recomended resolution 448px x 160px"
                                label={type === SettingsTypes.MOBILE ? 'Mobile Banner' : 'Web Banner'}
                                alt={type === SettingsTypes.MOBILE ? 'Mobile Banner' : 'Web Banner'}
                                img={getFilePreview(bannerSettings.content)}
                                onRemove={removeBannerImage}
                                onDrop={setBannerImage}
                                onDownload={downloadAdvertisementImage}
                            />
                        )}
                    </ValidatedControl>
                    <FormInput
                        label="Banner URL"
                        placeholder="Banner URL"
                        maxLength={128}
                        value={bannerSettings.link || ''}
                        onBlur={setBannerLink}
                    />
                </Group>
            </ServerResponseHandler>
        </Skeleton>
    );
};
